import { Form } from 'antd';
import { Greenhouse, ProductionItemDetail } from 'constants/interface';
import { Dayjs } from 'dayjs';
import { buildProductionItemQuantityDisplay } from 'helpers/order';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'hooks/store';
import {
  DatePicker,
  Input,
  Modal,
  Select,
} from 'UI';
import { updateSeedingScheduleAction } from 'state/slices/productionItem';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { unwrapResult } from '@reduxjs/toolkit';
import { getGreenhouseListAction, greenhousesEntitiesSelector, greenhousesIdsSelector } from 'state/slices/greenhouse';
import { useSelector } from 'react-redux';
import { formatDate, parseUtcStringDate } from 'helpers/utils';

import {
  InlineButton,
  MainContainer,
  FormContainer,
  ItemInfoContainer,
  ItemInfoRow,
  ItemInfoLabel,
  ItemInfoValue,
  BaseFormItem,
  FormItemRow,
} from './style';

interface SchedulingModalProps {
  item: ProductionItemDetail
  isVisible: boolean
  onCancel: () => void
  onSuccess: () => void
}

interface SeedingSchedulingFormData {
  seedingDate: Dayjs
  greenhouseId: string
  notes?: string
}

const SchedulingModal = ({
  item,
  isVisible,
  onCancel,
  onSuccess,
}: SchedulingModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);

  const [schedulingForm] = Form.useForm<SeedingSchedulingFormData>();

  const formattedPlannedDate = useMemo(() => {
    // const formattedDate = (new Date(item.orderItem.plannedSeedDate))
    //   .toLocaleDateString('pt-br');
    const formattedDate = formatDate(parseUtcStringDate(item.orderItem.plannedSeedDate));
    return formattedDate;
  }, [item]);

  useEffect(() => {
    if (isVisible) {
      dispatch(getGreenhouseListAction({
        isPaginated: false,
      }));
    }
  }, [isVisible]);

  const greenhouseIds = useSelector(greenhousesIdsSelector);
  const greenhouseEntities = useSelector(greenhousesEntitiesSelector);
  const greenhouseList = useMemo(() => {
    const curList = greenhouseIds
      .map((greenhouseId) => greenhouseEntities[greenhouseId])
      .filter((greenhouse): greenhouse is Greenhouse => !!greenhouse);
    return curList;
  }, [greenhouseIds, greenhouseEntities]);

  useEffect(() => {
    if (schedulingForm.getFieldValue('greenhouseId') === undefined) {
      schedulingForm.setFieldsValue({
        greenhouseId: greenhouseList[0]?.id,
      });
    }
  }, [greenhouseList, schedulingForm]);

  const mainSeedingBatch = item.seedingBatches ? item.seedingBatches[0] : undefined;

  const submitSchedule = async (): Promise<void> => {
    try {
      await schedulingForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }
    const formData = schedulingForm.getFieldsValue();
    // const mainSeedingBatch = item.seedingBatches ? item.seedingBatches[0] : undefined;

    if (!mainSeedingBatch) {
      return;
    }

    // const test = formData.seedingDate.hour(0).minute(0).second(0);
    // const left = test.subtract(12, 'hour');
    // const right = test.add(12, 'hour');
    // console.log(formData.seedingDate.toString());
    // console.log(formData.seedingDate.toISOString());
    // console.log(formData.seedingDate.format());
    // console.log(dayjs(formData.seedingDate.format(), 'DD-MM-YYYY').format());
    // console.log(dayjs(formData.seedingDate.toString()).toISOString());
    // console.log(dayjs(formData.seedingDate.format('YYYY-MM-DD')).toISOString());
    // console.log(test.toISOString());
    // console.log(left.toISOString());
    // console.log(right.toISOString());
    // console.log(dayjs(formData.seedingDate.format('YYYY-MM-DD')).toISOString());

    try {
      setLoading(true);
      const result = await dispatch(updateSeedingScheduleAction({
        id: mainSeedingBatch.id,
        data: {
          // seedingDate: formData.seedingDate.toISOString(),
          seedingDate: formData.seedingDate.format(),
          greenhouse: formData.greenhouseId,
          notes: formData.notes,
        },
      }));
      setLoading(false);
      unwrapResult(result);
      onSuccess();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar programar semeio. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Programação de semeio"
      destroyOnClose
      onCancel={onCancel}
      afterClose={onCancel}
      zIndex={90}
      footer={[
        <InlineButton
          key="cancel"
          onClick={onCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={submitSchedule}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <MainContainer>
        <ItemInfoContainer>
          <ItemInfoRow>
            <ItemInfoLabel>Cultivar:</ItemInfoLabel>
            <ItemInfoValue>{`${item.product.variety.culture.name} ${item.product.variety.name}`}</ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Quantidade prevista:</ItemInfoLabel>
            <ItemInfoValue>
              {`${buildProductionItemQuantityDisplay(item)}`}
            </ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Data prevista:</ItemInfoLabel>
            <ItemInfoValue>
              {formattedPlannedDate}
            </ItemInfoValue>
          </ItemInfoRow>
          {/* <ItemInfoRow>
            <ItemInfoLabel>Estoque:</ItemInfoLabel>
            <ItemInfoValue>
              {
                item.seedSource === InventoryTypeEnum.CLIENT
                  ? (
                    item.orderItem.order.client.name
                    || InventoryTypeDisplayMapping[InventoryTypeEnum.CLIENT].name
                  ) : (
                    InventoryTypeDisplayMapping[item.seedSource].name
                  )
              }
            </ItemInfoValue>
          </ItemInfoRow> */}
        </ItemInfoContainer>
        <FormContainer
          layout="vertical"
          form={schedulingForm}
          requiredMark={false}
          validateTrigger="onSubmit"
          initialValues={{
            seedingDate: mainSeedingBatch?.seedingDate
              ? parseUtcStringDate(mainSeedingBatch.seedingDate)
              : parseUtcStringDate(item.orderItem.plannedSeedDate),
            greenhouseId: mainSeedingBatch?.greenhouse?.id || undefined,
            notes: mainSeedingBatch?.notes || undefined,
          }}
        >
          <FormItemRow>
            <BaseFormItem
              label="Data programada"
              name="seedingDate"
              rules={[
                {
                  required: true,
                  message: 'Selecione uma data',
                },
              ]}
            >
              <DatePicker showTime={false} />
            </BaseFormItem>
            <BaseFormItem
              label="Estufa"
              name="greenhouseId"
              rules={[
                {
                  required: true,
                  message: 'Selecione uma estufa',
                },
              ]}
            >
              <Select
                defaultActiveFirstOption
              >
                {greenhouseList.map((curGreenhouse) => (
                  <Select.Option
                    key={curGreenhouse.id}
                    value={curGreenhouse.id}
                  >
                    {`${curGreenhouse.name} (${curGreenhouse.organization.name})`}
                  </Select.Option>
                ))}
              </Select>
            </BaseFormItem>
          </FormItemRow>
          <BaseFormItem
            label="Observações"
            name="notes"
          >
            <Input.TextArea />
          </BaseFormItem>
        </FormContainer>
      </MainContainer>
    </Modal>
  );
};

export default SchedulingModal;
